// A new trial admin goes through the Welcome to IMPROVE wizard before anything
// else (Muhammad, 5 Oct 2026). Until the wizard is finished, the left menu is
// disabled, the Setup guide button is hidden, and the router sends any other
// page back to /welcome. Finishing the wizard (its last step) is the only way
// out; "Skip for now" was removed with this.
//
// The truth is the server's onboarding progress (`completed`), not this
// browser's cache: a trial admin who finished on another device must never be
// locked out on a fresh browser. A finished flag in the cache is trusted on
// its own, because completion never un-happens.
//
// This is a guided first-run, not a security boundary: nothing here protects
// data, it decides what the screen offers.
import Vue from "vue";
import companyAPI from "@/api/company";
import {
  getOnboardingStorageKey,
  isOnboardingCompleted,
  isTrialCompanyAdmin,
  markOnboardingCompleted
} from "@/utils/onboarding";

// What a trial admin can still open before finishing the wizard: the wizard,
// paying (the wizard's Buy Now and the header's Buy IMPROVE), help, their own
// profile and password, and the trial-ended page. (Booking an IMPROVE Live
// webinar is a popup over the page, so it needs no entry here.)
const OPEN_WHILE_LOCKED = [
  "/welcome",
  "/billing",
  "/resources",
  "/profile/profile",
  "/profile/password",
  "/trial-ended"
];

// Reactive, so the menu and the Setup guide button re-render the moment the
// lock is settled or lifted.
const lock = Vue.observable({ account: null, locked: false });
let pending = null;

export function isOpenWhileLocked(path) {
  return OPEN_WHILE_LOCKED.indexOf(path) !== -1;
}

export function wizardLocked() {
  return lock.locked;
}

// The wizard's last step: lift the lock before navigating away.
export function unlockWizard() {
  lock.locked = false;
}

// Settles the lock for the signed-in account: once per account per page load,
// shared by every caller while the request is in flight. Resolves to true when
// the account must stay on the wizard.
export function resolveWizardLock(storeGetters) {
  if (!isTrialCompanyAdmin(storeGetters)) {
    lock.locked = false;
    return Promise.resolve(false);
  }
  const account = getOnboardingStorageKey();
  if (lock.account === account) {
    return pending || Promise.resolve(lock.locked);
  }
  lock.account = account;
  if (isOnboardingCompleted()) {
    lock.locked = false;
    pending = null;
    return Promise.resolve(false);
  }
  pending = companyAPI
    .getOnboardingProgress()
    .then(function(resp) {
      const data = resp && resp.data && resp.data.data ? resp.data.data : {};
      if (data.completed) markOnboardingCompleted();
      return !data.completed;
    })
    .catch(function() {
      // Fail open: if the progress cannot be read, a stuck admin is worse
      // than one who reaches the menu before finishing the wizard.
      return false;
    })
    .then(function(locked) {
      if (lock.account === account) {
        lock.locked = locked;
        pending = null;
      }
      return locked;
    });
  return pending;
}
